import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import {
  minutesToTime,
  snapToQuarterHour,
  timeToMinutes,
} from '../WeeklyScheduleEditor';


const SNAP_MINUTES = 15;
const HOUR_HEIGHT = 48;
const MINUTE_HEIGHT = HOUR_HEIGHT / 60;
const CALENDAR_HEIGHT = 24 * HOUR_HEIGHT;
const VISIBLE_HOURS = 6;
const CALENDAR_HEADER_HEIGHT = 52;
const LATEST_END_MINUTES = (24 * 60) - SNAP_MINUTES;

const formatTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${String(minutes % 60).padStart(2, '0')} ${suffix}`;
};

const valueMinutes = (value) => timeToMinutes(String(value || '').slice(11, 16));

const pointerMinutes = (event, element) => {
  const rect = element.getBoundingClientRect();
  return Math.max(0, Math.min(
    LATEST_END_MINUTES - 1,
    (event.clientY - rect.top) / MINUTE_HEIGHT,
  ));
};

const pickerPaperSx = (theme) => ({
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.paper,
  '& .MuiTypography-root, & .MuiPickersCalendarHeader-label, & .MuiDayCalendar-weekDayLabel': {
    color: theme.palette.primary.main,
  },
  '& .MuiPickersDay-root': {
    color: theme.palette.primary.main,
  },
  '& .MuiPickersDay-root.Mui-selected': {
    color: theme.palette.background.default,
    backgroundColor: `${theme.palette.primary.main} !important`,
  },
});

export default function EventDateTimePicker({ startsAt, endsAt, onChange }) {
  const calendarViewportRef = useRef(null);
  const dragRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(
    String(startsAt || endsAt).slice(0, 10) || dayjs().format('YYYY-MM-DD'),
  );
  const [draft, setDraft] = useState(null);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (calendarViewportRef.current) {
      calendarViewportRef.current.scrollTop = 9 * HOUR_HEIGHT;
    }
  }, []);

  useEffect(() => {
    const incomingDate = String(startsAt || endsAt).slice(0, 10);
    if (incomingDate) setSelectedDate(incomingDate);
  }, [startsAt, endsAt]);

  const savedRange = startsAt && endsAt && startsAt.slice(0, 10) === endsAt.slice(0, 10)
    ? { start: valueMinutes(startsAt), end: valueMinutes(endsAt) }
    : null;
  const hasSavedRange = savedRange && savedRange.end > savedRange.start;
  const displayRange = draft || (hasSavedRange ? savedRange : null);

  const applyRange = (range, date = selectedDate) => {
    onChange('startsAt', `${date}T${minutesToTime(range.start)}`);
    onChange('endsAt', `${date}T${minutesToTime(range.end)}`);
  };

  const changeDate = (value) => {
    if (!value?.isValid()) return;
    const nextDate = value.format('YYYY-MM-DD');
    setSelectedDate(nextDate);
    if (displayRange) applyRange(displayRange, nextDate);
  };

  const beginCreate = (event) => {
    if (
      event.button !== 0
      || event.target.closest?.('[data-event-block="true"]')
    ) return;
    const slot = Math.min(
      (LATEST_END_MINUTES / SNAP_MINUTES) - 1,
      Math.floor(pointerMinutes(event, event.currentTarget) / SNAP_MINUTES),
    );
    dragRef.current = {
      type: 'create',
      pointerId: event.pointerId,
      anchor: slot * SNAP_MINUTES,
      element: event.currentTarget,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraft({ start: slot * SNAP_MINUTES, end: (slot + 1) * SNAP_MINUTES });
    setFeedback('');
    event.preventDefault();
  };

  const beginResize = (event, edge) => {
    if (!displayRange) return;
    event.stopPropagation();
    const column = event.currentTarget.closest('[data-day-column="true"]');
    dragRef.current = {
      type: 'resize',
      pointerId: event.pointerId,
      edge,
      start: displayRange.start,
      end: displayRange.end,
      element: column,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraft(displayRange);
    setFeedback('');
    event.preventDefault();
  };

  const beginMove = (event) => {
    if (!displayRange || event.button !== 0) return;
    event.stopPropagation();
    const column = event.currentTarget.closest('[data-day-column="true"]');
    dragRef.current = {
      type: 'move',
      pointerId: event.pointerId,
      pointerStart: pointerMinutes(event, column),
      start: displayRange.start,
      end: displayRange.end,
      element: column,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraft(displayRange);
    setFeedback('');
    event.preventDefault();
  };

  const updateDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const minutes = pointerMinutes(event, drag.element);
    if (drag.type === 'create') {
      const snappedEnd = Math.min(
        LATEST_END_MINUTES,
        (Math.floor(minutes / SNAP_MINUTES) + 1) * SNAP_MINUTES,
      );
      setDraft({
        start: drag.anchor,
        end: Math.max(drag.anchor + SNAP_MINUTES, snappedEnd),
      });
      return;
    }

    if (drag.type === 'move') {
      const duration = drag.end - drag.start;
      const delta = Math.round((minutes - drag.pointerStart) / SNAP_MINUTES) * SNAP_MINUTES;
      const start = Math.max(0, Math.min(LATEST_END_MINUTES - duration, drag.start + delta));
      setDraft({ start, end: start + duration });
      return;
    }

    const snapped = snapToQuarterHour(minutes);
    setDraft({
      start: drag.edge === 'start'
        ? Math.min(snapped, drag.end - SNAP_MINUTES)
        : drag.start,
      end: drag.edge === 'end'
        ? Math.max(snapped, drag.start + SNAP_MINUTES)
        : drag.end,
    });
  };

  const finishDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !draft) return;
    applyRange(draft);
    dragRef.current = null;
    setDraft(null);
    setFeedback('');
  };

  const cancelDrag = () => {
    dragRef.current = null;
    setDraft(null);
  };

  const updateExactTime = (field, value) => {
    if (!displayRange || !/^\d{2}:\d{2}$/.test(value)) return;
    const minutes = snapToQuarterHour(timeToMinutes(value));
    const nextRange = {
      ...displayRange,
      [field]: minutes,
    };
    if (nextRange.end <= nextRange.start) {
      setFeedback('The end time must be after the start time.');
      return;
    }
    applyRange(nextRange);
    setFeedback('');
  };

  const pickerSlotProps = {
    textField: {
      fullWidth: true,
      required: true,
      sx: {
        '& .MuiInputBase-root': { borderRadius: 3 },
        '& .MuiInputBase-input': { color: 'primary.main' },
        '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionBefore, & .MuiPickersInputBase-sectionAfter': {
          color: 'primary.main',
        },
      },
    },
    openPickerButton: { sx: { color: 'primary.main' } },
    popper: { sx: (theme) => ({ '& .MuiPaper-root': pickerPaperSx(theme) }) },
    mobilePaper: { sx: pickerPaperSx },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack
        spacing={1.25}
        sx={(theme) => ({
          p: 1.5,
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: 3,
          backgroundColor: lighten(theme.palette.background.paper, 0.06),
          boxShadow: `0 0 14px ${alpha(theme.palette.primary.main, 0.25)}`,
        })}
      >
        {feedback && (
          <Typography role="alert" variant="body2" sx={{ color: 'secondary.main' }}>
            {feedback}
          </Typography>
        )}

        <Box
          ref={calendarViewportRef}
          data-testid="event-day-viewport"
          sx={(theme) => ({
            maxHeight: (VISIBLE_HOURS * HOUR_HEIGHT) + CALENDAR_HEADER_HEIGHT,
            overflowY: 'auto',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.68)}`,
            borderRadius: 3,
            backgroundColor: theme.palette.background.default,
            boxShadow: `inset 0 0 24px rgba(0, 0, 0, 0.5), 0 0 18px ${alpha(theme.palette.primary.main, 0.14)}`,
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.secondary.main} ${theme.palette.background.default}`,
          })}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={(theme) => ({
              position: 'sticky',
              top: 0,
              zIndex: 5,
              minHeight: CALENDAR_HEADER_HEIGHT,
              px: 1,
              py: 0.5,
              textAlign: 'center',
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.background.default,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              boxShadow: '0 5px 12px rgba(0, 0, 0, 0.55)',
            })}
          >
            <Typography variant="caption" sx={{ color: 'primary.main' }}>
              {dayjs(selectedDate).format('dddd, MMMM D')}
            </Typography>
            <DatePicker
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={changeDate}
              slotProps={{
                ...pickerSlotProps,
                textField: {
                  ...pickerSlotProps.textField,
                  fullWidth: false,
                  size: 'small',
                  'data-testid': 'event-date-picker-control',
                  sx: {
                    ...pickerSlotProps.textField.sx,
                    width: 44,
                    '& .MuiPickersInputBase-root': {
                      width: 44,
                      minWidth: 44,
                      p: 0,
                      borderRadius: '50%',
                      backgroundColor: 'transparent',
                    },
                    '& .MuiPickersInputBase-sectionsContainer': {
                      display: 'none',
                    },
                    '& .MuiPickersOutlinedInput-notchedOutline': {
                      display: 'none',
                    },
                    '& .MuiInputAdornment-root': {
                      m: 0,
                    },
                    '& .MuiIconButton-root': {
                      m: 0,
                      p: 1,
                      color: 'primary.main',
                    },
                  },
                },
              }}
            />
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: '74px minmax(0, 1fr)', gap: '2px', p: '2px' }}>
            <Box
              aria-hidden="true"
              sx={(theme) => ({
                position: 'relative',
                height: CALENDAR_HEIGHT,
                backgroundColor: theme.palette.background.paper,
                boxShadow: '5px 0 10px rgba(0, 0, 0, 0.5)',
              })}
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <Typography
                  key={hour}
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: hour * HOUR_HEIGHT,
                    right: 8,
                    transform: 'translateY(-1px)',
                    color: 'secondary.main',
                    fontSize: '0.58rem',
                  }}
                >
                  {formatTime(hour * 60)}
                </Typography>
              ))}
            </Box>

            <Box
              role="gridcell"
              aria-label="Event day schedule"
              data-day-column="true"
              onPointerDown={beginCreate}
              onPointerMove={updateDrag}
              onPointerUp={finishDrag}
              onPointerCancel={cancelDrag}
              sx={(theme) => ({
                position: 'relative',
                height: CALENDAR_HEIGHT,
                touchAction: 'none',
                cursor: 'crosshair',
                userSelect: 'none',
                background: `repeating-linear-gradient(
                  to bottom,
                  ${lighten(theme.palette.background.paper, 0.045)} 0,
                  ${lighten(theme.palette.background.paper, 0.045)} ${HOUR_HEIGHT}px,
                  ${theme.palette.background.paper} ${HOUR_HEIGHT}px,
                  ${theme.palette.background.paper} ${HOUR_HEIGHT * 2}px
                )`,
                borderRadius: 1,
              })}
            >
              {displayRange && (
                <Box
                  data-event-block="true"
                  data-testid="event-time-block"
                  onPointerDown={beginMove}
                  onPointerMove={updateDrag}
                  onPointerUp={finishDrag}
                  onPointerCancel={cancelDrag}
                  sx={(theme) => ({
                    position: 'absolute',
                    zIndex: 2,
                    top: displayRange.start * MINUTE_HEIGHT,
                    left: 6,
                    right: 6,
                    height: Math.max(12, (displayRange.end - displayRange.start) * MINUTE_HEIGHT),
                    minHeight: 12,
                    px: 1,
                    py: 0.6,
                    overflow: 'visible',
                    color: theme.palette.primary.main,
                    border: `2px solid ${draft ? theme.palette.secondary.main : theme.palette.primary.main}`,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${darken(theme.palette.primary.main, 0.66)}, ${theme.palette.background.default})`,
                    boxShadow: `0 0 0 2px ${theme.palette.green?.main || '#39FF14'}, 0 0 16px ${alpha(theme.palette.primary.main, 0.55)}`,
                    cursor: 'move',
                    touchAction: 'none',
                  })}
                >
                  <Box
                    role="slider"
                    aria-label="Change event start time"
                    aria-valuemin={0}
                    aria-valuemax={displayRange.end - SNAP_MINUTES}
                    aria-valuenow={displayRange.start}
                    onPointerDown={(event) => beginResize(event, 'start')}
                    onPointerMove={updateDrag}
                    onPointerUp={finishDrag}
                    onPointerCancel={cancelDrag}
                    sx={(theme) => ({
                      position: 'absolute',
                      zIndex: 3,
                      top: -10,
                      left: 0,
                      right: 0,
                      height: 20,
                      cursor: 'ns-resize',
                      touchAction: 'none',
                      backgroundColor: 'transparent',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 9,
                        left: 0,
                        right: 0,
                        height: 2,
                        backgroundColor: alpha(theme.palette.green?.main || '#39FF14', 0.8),
                      },
                    })}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: 'primary.main', display: 'block', overflow: 'hidden' }}
                  >
                    {formatTime(displayRange.start)} – {formatTime(displayRange.end)}
                  </Typography>
                  <Box
                    role="slider"
                    aria-label="Change event end time"
                    aria-valuemin={displayRange.start + SNAP_MINUTES}
                    aria-valuemax={LATEST_END_MINUTES}
                    aria-valuenow={displayRange.end}
                    onPointerDown={(event) => beginResize(event, 'end')}
                    onPointerMove={updateDrag}
                    onPointerUp={finishDrag}
                    onPointerCancel={cancelDrag}
                    sx={(theme) => ({
                      position: 'absolute',
                      zIndex: 3,
                      bottom: -10,
                      left: 0,
                      right: 0,
                      height: 20,
                      cursor: 'ns-resize',
                      touchAction: 'none',
                      backgroundColor: 'transparent',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 9,
                        left: 0,
                        right: 0,
                        height: 2,
                        backgroundColor: alpha(theme.palette.green?.main || '#39FF14', 0.8),
                      },
                    })}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {displayRange && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              label="Start time"
              type="time"
              fullWidth
              value={minutesToTime(displayRange.start)}
              onChange={(event) => updateExactTime('start', event.target.value)}
              inputProps={{ step: SNAP_MINUTES * 60 }}
              sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
            />
            <TextField
              label="End time"
              type="time"
              fullWidth
              value={minutesToTime(displayRange.end)}
              onChange={(event) => updateExactTime('end', event.target.value)}
              inputProps={{ step: SNAP_MINUTES * 60 }}
              sx={{ '& .MuiInputBase-input': { color: 'primary.main' } }}
            />
          </Stack>
        )}
      </Stack>
    </LocalizationProvider>
  );
}
